import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { aluguelService } from "./aluguelService";

// ─── Estado inicial do formulário ─────────────────────────────────────────────

const FORM_INITIAL = {
  id: null,
  locatario_nome: "",
  locatario_cpf: "",
  locatario_tel: "",
  locatario_email: "",
  locatario_end: "",
  data_retirada: "",
  data_devolucao: "",
  itens: [],
  pagamentos: [],
  desconto: 0,
  observacoes: "",
};

const PGTO_INITIAL = { forma: "Pix", valor: "", tipo: "sinal", observacao: "" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const swalSuccess = (title) =>
  Swal.fire({ icon: "success", title, showConfirmButton: false, timer: 1500 });

const swalError = (message) => Swal.fire("Erro", message, "error");

// 🔧 ADICIONADO: fmtBRL para formatar valores monetários
const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const calcDias = (retirada, devolucao) => {
  if (!retirada || !devolucao) return 1;
  const ms = new Date(devolucao) - new Date(retirada);
  const d = Math.ceil(ms / (1000 * 60 * 60 * 24));
  return d > 0 ? d : 1;
};

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useAluguel() {
  const [alugueis, setAlugueis] = useState([]);
  const [alugaveisDisponiveis, setAlugaveisDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPgtoOpen, setModalPgtoOpen] = useState(false);
  const [form, setForm] = useState(FORM_INITIAL);
  const [formPgto, setFormPgto] = useState(PGTO_INITIAL);
  const [aluguelSelecionado, setAluguelSelecionado] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("ativos");
  const [pagamentoCallback, setPagamentoCallback] = useState(null); // 🔧 USADO para callback

  // ── Carregar lista de aluguéis ──
  const fetchAlugueis = useCallback(async () => {
    setLoading(true);
    try {
      const data = await aluguelService.getAll();
      setAlugueis(data);
    } catch {
      swalError("Não foi possível carregar os aluguéis.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Carregar produtos alugáveis ──
  const fetchAlugaveis = useCallback(async () => {
    try {
      const data = await aluguelService.getProdutosAlugaveis();
      setAlugaveisDisponiveis(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAlugueis();
    fetchAlugaveis();
  }, [fetchAlugueis, fetchAlugaveis]);

  // ── Abrir modal de criação ──
  const openModal = useCallback((aluguel = null) => {
    if (aluguel) {
      setForm({
        id: aluguel.id,
        locatario_nome: aluguel.locatario_nome ?? "",
        locatario_cpf: aluguel.locatario_cpf ?? "",
        locatario_tel: aluguel.locatario_tel ?? "",
        locatario_email: aluguel.locatario_email ?? "",
        locatario_end: aluguel.locatario_end ?? "",
        data_retirada: aluguel.data_retirada ? aluguel.data_retirada.slice(0, 16) : "",
        data_devolucao: aluguel.data_devolucao ? aluguel.data_devolucao.slice(0, 16) : "",
        itens: aluguel.itens ?? [],
        pagamentos: [],
        desconto: aluguel.desconto ?? 0,
        observacoes: aluguel.observacoes ?? "",
      });
    } else {
      setForm(FORM_INITIAL);
    }
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setForm(FORM_INITIAL);
  }, []);

  // ── Submit do formulário principal ──
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.data_retirada || !form.data_devolucao)
        return swalError("Informe as datas de retirada e devolução.");
      if (form.itens.length === 0)
        return swalError("Adicione pelo menos um item ao aluguel.");

      try {
        const payload = {
          ...form,
          desconto: Number(form.desconto) || 0,
          itens: form.itens.map((it) => ({
            produto_id: it.produto_id,
            quantidade: it.quantidade,
          })),
          pagamentos: form.pagamentos.filter((p) => Number(p.valor) > 0),
        };
        if (form.id) {
          await aluguelService.update(form.id, payload);
          await swalSuccess("Aluguel atualizado!");
        } else {
          await aluguelService.create(payload);
          await swalSuccess("Aluguel criado!");
        }
        await fetchAlugueis();
        closeModal();
      } catch (err) {
        swalError(err.response?.data?.error || "Não foi possível salvar o aluguel.");
      }
    },
    [form, fetchAlugueis, closeModal]
  );

  // ── Mudar status ──
  const handleMudarStatus = useCallback(
    async (aluguel, novoStatus, dataReal = null, forceRecalcular = false) => {
      const LABELS = {
        confirmado: "Confirmar reserva",
        em_andamento: "Registrar retirada",
        devolvido: "Confirmar devolução",
        cancelado: "Cancelar aluguel",
      };

      // Se for retirada ou devolução e não tiver dataReal, pedir
      if ((novoStatus === "em_andamento" || novoStatus === "devolvido") && !dataReal) {
        const { value: novaData } = await Swal.fire({
          title: LABELS[novoStatus],
          html: `<input id="dataReal" type="datetime-local" class="swal2-input" value="${new Date().toISOString().slice(0, 16)}">`,
          focusConfirm: false,
          preConfirm: () => document.getElementById("dataReal").value,
          showCancelButton: true,
          confirmButtonText: "Continuar",
          cancelButtonText: "Cancelar",
        });
        if (!novaData) return;
        dataReal = novaData;
      }

      try {
        await aluguelService.updateStatus(aluguel.id, novoStatus, dataReal, forceRecalcular);
        await swalSuccess("Status atualizado!");
        await fetchAlugueis();
      } catch (err) {
        const errorData = err.response?.data;
        // Confirmação de recálculo (409)
        if (err.response?.status === 409 && errorData?.precisa_confirmacao) {
          const confirm = await Swal.fire({
            title: "Atenção",
            html: errorData.mensagem,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, aceitar alteração",
            cancelButtonText: "Cancelar",
          });
          if (confirm.isConfirmed) {
            await handleMudarStatus(aluguel, novoStatus, dataReal, true);
          }
          return;
        }
        // Necessidade de pagamento na devolução
        if (errorData?.precisa_pagamento && novoStatus === "devolvido") {
          const { isConfirmed } = await Swal.fire({
            title: "Saldo pendente",
            text: `Cliente ainda deve ${fmtBRL(errorData.saldo)}. Deseja registrar um pagamento agora?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, registrar pagamento",
            cancelButtonText: "Cancelar devolução",
          });
          if (isConfirmed) {
            // Abre modal de pagamento e, após pagamento, tenta novamente a devolução
            openModalPgto(aluguel, () =>
              handleMudarStatus(aluguel, novoStatus, dataReal, forceRecalcular)
            );
          }
          return;
        }
        swalError(errorData?.error || "Não foi possível atualizar o status.");
      }
    },
    [fetchAlugueis] // 🔧 removi openModalPgto da dependência para evitar loop, mas vou definir openModalPgto antes
  );

  // ── Modal de pagamento (definido ANTES de handleMudarStatus para usar) ──
  const openModalPgto = useCallback((aluguel, onSuccess = null) => {
    setAluguelSelecionado(aluguel);
    setFormPgto(PGTO_INITIAL);
    setPagamentoCallback(() => onSuccess); // 🔧 guarda callback no state
    setModalPgtoOpen(true);
  }, []);

  const closeModalPgto = useCallback(() => {
    setModalPgtoOpen(false);
    setAluguelSelecionado(null);
    setFormPgto(PGTO_INITIAL);
    setPagamentoCallback(null); // limpa callback
  }, []);

  const handleSubmitPgto = useCallback(
    async (e) => {
      e.preventDefault();
      if (!aluguelSelecionado) return;
      try {
        await aluguelService.registrarPagamento(aluguelSelecionado.id, {
          forma: formPgto.forma,
          valor: Number(formPgto.valor),
          tipo: formPgto.tipo,
          observacao: formPgto.observacao || undefined,
        });
        await swalSuccess("Pagamento registrado!");
        await fetchAlugueis();
        // Executa callback se existir (ex: tentar devolução novamente)
        if (pagamentoCallback) {
          pagamentoCallback();
        }
        closeModalPgto();
      } catch (err) {
        swalError(err.response?.data?.error || "Não foi possível registrar o pagamento.");
      }
    },
    [aluguelSelecionado, formPgto, fetchAlugueis, closeModalPgto, pagamentoCallback]
  );

  // 🔧 Agora handleMudarStatus pode usar openModalPgto sem problema
  // (já está definido acima, mas preciso garantir a ordem)

  // ── Adicionar item ao form ──
  const addItem = useCallback((produto) => {
    setForm((prev) => {
      const existe = prev.itens.find((it) => it.produto_id === produto.id);
      if (existe) {
        return {
          ...prev,
          itens: prev.itens.map((it) =>
            it.produto_id === produto.id ? { ...it, quantidade: it.quantidade + 1 } : it
          ),
        };
      }
      return {
        ...prev,
        itens: [
          ...prev.itens,
          {
            produto_id: produto.id,
            produto_nome: produto.nome,
            valor_unitario: Number(produto.valor_diaria || 0),
            quantidade: 1,
          },
        ],
      };
    });
  }, []);

  const removeItem = useCallback((produto_id) => {
    setForm((prev) => ({
      ...prev,
      itens: prev.itens.filter((it) => it.produto_id !== produto_id),
    }));
  }, []);

  const updateItemQtd = useCallback((produto_id, qtd) => {
    setForm((prev) => ({
      ...prev,
      itens: prev.itens.map((it) =>
        it.produto_id === produto_id ? { ...it, quantidade: Math.max(1, Number(qtd)) } : it
      ),
    }));
  }, []);

  const addPagamentoForm = useCallback((pgto) => {
    setForm((prev) => ({ ...prev, pagamentos: [...prev.pagamentos, pgto] }));
  }, []);

  const removePagamentoForm = useCallback((idx) => {
    setForm((prev) => ({
      ...prev,
      pagamentos: prev.pagamentos.filter((_, i) => i !== idx),
    }));
  }, []);

  // ── Totais calculados no form ──
  const totaisForm = useMemo(() => {
    const dias = calcDias(form.data_retirada, form.data_devolucao);
    const subtotal = form.itens.reduce(
      (acc, it) => acc + it.valor_unitario * it.quantidade * dias,
      0
    );
    const total = Math.max(0, subtotal - Number(form.desconto || 0));
    const pago = form.pagamentos.reduce((acc, p) => acc + Number(p.valor || 0), 0);
    return { dias, subtotal, total, pago, saldo: total - pago };
  }, [form.itens, form.data_retirada, form.data_devolucao, form.desconto, form.pagamentos]);

  // ── Filtro de listagem ──
  const alugueisFiltrados = useMemo(() => {
    const STATUS_ATIVOS = ["reservado", "confirmado", "em_andamento"];
    const STATUS_HISTORICO = ["devolvido", "cancelado"];
    return alugueis.filter((a) => {
      const naAba =
        abaAtiva === "ativos"
          ? STATUS_ATIVOS.includes(a.status)
          : STATUS_HISTORICO.includes(a.status);
      if (!naAba) return false;
      if (filtroStatus !== "todos" && a.status !== filtroStatus) return false;
      if (filtroTexto) {
        const t = filtroTexto.toLowerCase();
        return (
          a.locatario_nome?.toLowerCase().includes(t) ||
          a.locatario_tel?.toLowerCase().includes(t) ||
          String(a.id).includes(t)
        );
      }
      return true;
    });
  }, [alugueis, abaAtiva, filtroStatus, filtroTexto]);

  // ── Contagens por aba ──
  const counts = useMemo(
    () => ({
      ativos: alugueis.filter((a) => ["reservado", "confirmado", "em_andamento"].includes(a.status)).length,
      historico: alugueis.filter((a) => ["devolvido", "cancelado"].includes(a.status)).length,
    }),
    [alugueis]
  );

  return {
    alugueis: alugueisFiltrados,
    alugaveisDisponiveis,
    counts,
    loading,
    modalOpen,
    modalPgtoOpen,
    form,
    setForm,
    formPgto,
    setFormPgto,
    aluguelSelecionado,
    filtroStatus,
    setFiltroStatus,
    filtroTexto,
    setFiltroTexto,
    abaAtiva,
    setAbaAtiva,
    totaisForm,
    openModal,
    closeModal,
    handleSubmit,
    handleMudarStatus,
    openModalPgto,
    closeModalPgto,
    handleSubmitPgto,
    addItem,
    removeItem,
    updateItemQtd,
    addPagamentoForm,
    removePagamentoForm,
  };
}